import supabase from '../supabase/supabase';

class BankRulesService {
  async getAccountId(preferredAccountId) {
    const { data: { user }, error: authError } = await supabase.auth.getUser();
    if (authError || !user) throw new Error("Authentication required.");

    const activeAccountId = preferredAccountId || (typeof window !== 'undefined'
      ? localStorage.getItem('activeClientId')
      : null);

    if (activeAccountId) {
      return activeAccountId;
    }

    const { data, error } = await supabase
      .from('profiles_20240320')
      .select('account_id')
      .eq('id', user.id)
      .single();

    if (error || !data?.account_id) {
      throw new Error("Business account not found. Please complete setup.");
    }
    return data.account_id;
  }

  async getRules() {
    try {
      const account_id = await this.getAccountId();
      const { data, error } = await supabase
        .schema('accounting')
        .from('bank_rules')
        .select('*')
        .eq('account_id', account_id)
        .order('priority', { ascending: true })
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (err) {
      console.error("Fetch Bank Rules Error:", err);
      throw new Error("Failed to load bank rules.");
    }
  }

  async getTestTransactions() {
    try {
      const account_id = await this.getAccountId();
      const { data, error } = await supabase
        .schema('accounting')
        .from('transactions_20240522')
        .select(`
          id,
          batch_id,
          date,
          description,
          memo,
          payee,
          debit,
          credit,
          coa_id,
          chart_of_accounts:coa_id (
            name,
            type
          )
        `)
        .eq('account_id', account_id)
        .order('date', { ascending: false })
        .limit(100);

      if (error) throw error;
      return data || [];
    } catch (err) {
      console.error("Test Bank Rule Error:", err);
      throw new Error("Failed to load transactions for testing.");
    }
  }

  async testRule(rule) {
    const transactions = await this.getTestTransactions();
    const conditions = rule.conditions || [];

    const matchesCondition = (transaction, condition) => {
      const amount = Math.max(
        Number(transaction.debit || 0),
        Number(transaction.credit || 0)
      );
      const transactionType = Number(transaction.credit || 0) > 0
        ? 'Money out'
        : Number(transaction.debit || 0) > 0
          ? 'Money in'
          : 'All';

      const actualValue = condition.field === 'amount'
        ? amount
        : condition.field === 'bank_text'
          ? transaction.memo || transaction.description || ''
          : condition.field === 'payee'
            ? transaction.payee || ''
            : condition.field === 'category'
              ? transaction.chart_of_accounts?.name || ''
              : condition.field === 'transaction_type'
                ? transactionType
                : transaction.description || '';

      const actual = String(actualValue).toLowerCase();
      const expected = String(condition.value || '').toLowerCase();

      switch (condition.operator) {
        case 'contains':
          return actual.includes(expected);
        case 'not_contains':
          return !actual.includes(expected);
        case 'equals':
          return actual === expected;
        case 'not_equals':
          return actual !== expected;
        case 'starts_with':
          return actual.startsWith(expected);
        case 'ends_with':
          return actual.endsWith(expected);
        case 'greater_than':
          return Number(actualValue) > Number(condition.value);
        case 'less_than':
          return Number(actualValue) < Number(condition.value);
        default:
          return false;
      }
    };

    return transactions
      .filter((transaction) => {
        if (
          rule.applied_account_ids?.length &&
          !rule.applied_account_ids.includes(transaction.coa_id)
        ) {
          return false;
        }

        const transactionType = Number(transaction.credit || 0) > 0
          ? 'Money out'
          : Number(transaction.debit || 0) > 0
            ? 'Money in'
            : 'All';

        if (
          rule.transaction_type &&
          rule.transaction_type !== 'All' &&
          rule.transaction_type !== transactionType
        ) {
          return false;
        }

        const results = conditions.map((condition) => (
          matchesCondition(transaction, condition)
        ));

        return rule.match_type === 'ANY'
          ? results.some(Boolean)
          : results.length > 0 && results.every(Boolean);
      })
      .slice(0, 10)
      .map((transaction) => ({
        ...transaction,
        match_status: 'Matches rule'
      }));
  }

  async applyRuleToExistingTransactions(ruleId) {
    const { data, error } = await supabase.rpc(
      'apply_bank_rule_to_existing_transactions',
      { p_rule_id: ruleId }
    );

    if (error) {
      console.error("Apply Bank Rule Error:", error);
      throw new Error(
        error.message || "The rule was saved but could not be applied to existing transactions."
      );
    }

    return data || 0;
  }

  async createRule(payload, activeAccountId) {
    try {
      const account_id = await this.getAccountId(activeAccountId);

      const { data: existingRules } = await supabase
        .schema('accounting')
        .from('bank_rules')
        .select('priority')
        .eq('account_id', account_id)
        .order('priority', { ascending: false })
        .limit(1);

      const nextPriority = existingRules && existingRules.length > 0
        ? existingRules[0].priority + 1
        : 1;

      const { data, error } = await supabase
        .schema('accounting')
        .from('bank_rules')
        .insert([{
          account_id,
          name: payload.name,
          match_type: payload.match_type || 'ALL',
          conditions: payload.conditions || [],
          assign_category: payload.assign_category,
          assigned_category_id: payload.assigned_category_id || null,
          applied_account_ids: payload.applied_account_ids || [],
          transaction_type: payload.transaction_type || 'All',
          action_transaction_type: payload.action_transaction_type || 'Expense',
          action_category_id: payload.assigned_category_id || null,
          action_payee_name: payload.action_payee || '',
          memo_action: payload.memo_action || 'keep',
          memo_value: payload.memo_value || '',
          split_lines: payload.split_lines || [],
          auto_add: payload.auto_add || false,
          priority: nextPriority,
          is_active: true
        }])
        .select()
        .single();

      if (error) throw error;

      await this.applyRuleToExistingTransactions(data.id);
      return data;
    } catch (err) {
      console.error("Create Rule Error:", err);
      throw err;
    }
  }

  async updateRule(id, payload, activeAccountId) {
    try {
      const account_id = await this.getAccountId(activeAccountId);

      const { data, error } = await supabase
        .schema('accounting')
        .from('bank_rules')
        .update({
          name: payload.name,
          match_type: payload.match_type,
          conditions: payload.conditions,
          assign_category: payload.assign_category,
          assigned_category_id: payload.assigned_category_id || null,
          applied_account_ids: payload.applied_account_ids || [],
          transaction_type: payload.transaction_type || 'All',
          action_transaction_type: payload.action_transaction_type || 'Expense',
          action_category_id: payload.assigned_category_id || null,
          action_payee_name: payload.action_payee || '',
          memo_action: payload.memo_action || 'keep',
          memo_value: payload.memo_value || '',
          split_lines: payload.split_lines || [],
          auto_add: payload.auto_add
        })
        .eq('id', id)
        .eq('account_id', account_id)
        .select()
        .single();

      if (error) throw error;

      await this.applyRuleToExistingTransactions(data.id);
      return data;
    } catch (err) {
      console.error("Update Rule Error:", err);
      throw err;
    }
  }

  async toggleAutoAdd(id, currentStatus) {
    try {
      const account_id = await this.getAccountId();
      const { error } = await supabase
        .schema('accounting')
        .from('bank_rules')
        .update({ auto_add: !currentStatus })
        .eq('id', id)
        .eq('account_id', account_id);

      if (error) throw error;
      return true;
    } catch (err) {
      console.error("Toggle Auto-Add Error:", err);
      throw new Error("Failed to update auto-add status.");
    }
  }

  async deleteRule(id) {
    try {
      const account_id = await this.getAccountId();
      const { error } = await supabase
        .schema('accounting')
        .from('bank_rules')
        .delete()
        .eq('id', id)
        .eq('account_id', account_id);

      if (error) throw error;
      return true;
    } catch (err) {
      console.error("Delete Rule Error:", err);
      throw new Error("Failed to delete rule.");
    }
  }

  async moveRulePriority(id, direction, currentPriority) {
    try {
      const account_id = await this.getAccountId();
      const targetPriority = direction === 'up'
        ? currentPriority - 1
        : currentPriority + 1;

      const { data: swapTarget } = await supabase
        .schema('accounting')
        .from('bank_rules')
        .select('id')
        .eq('account_id', account_id)
        .eq('priority', targetPriority)
        .maybeSingle();

      if (swapTarget) {
        await supabase
          .schema('accounting')
          .from('bank_rules')
          .update({ priority: currentPriority })
          .eq('id', swapTarget.id);
      }

      await supabase
        .schema('accounting')
        .from('bank_rules')
        .update({ priority: targetPriority })
        .eq('id', id);

      return true;
    } catch (err) {
      console.error("Move Priority Error:", err);
      throw new Error("Failed to reorder rule.");
    }
  }
}

export const bankRulesService = new BankRulesService();